import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, StickyNote, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUSES = [
  { value: "nuovo", label: "Nuovo" },
  { value: "contattato", label: "Contattato" },
  { value: "trattativa", label: "In trattativa" },
  { value: "prenotato", label: "Prenotato" },
  { value: "in_spedizione", label: "In spedizione" },
  { value: "archiviato", label: "Archiviato" },
];

const APP_STATUSES = [
  { value: "ricevuta", label: "Ricevuta" },
  { value: "valutazione", label: "In valutazione" },
  { value: "accettata", label: "Accettata" },
  { value: "rifiutata", label: "Rifiutata" },
];

const EMPTY = { name: "", email: "", phone: "", destination_interest: "", budget: "", message: "", status: "nuovo" };

export default function AdminCRM() {
  const isAdmin = useAuth().user?.role === "admin";

  const [view, setView] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ ...EMPTY });
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [appDetail, setAppDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    return api.get("/admin/crm/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  };
  const loadApps = () => api.get("/admin/crm/applications").then((r) => setApps(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/admin/crm/stats").then((r) => setStats(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === "apps") loadApps();
  }, [view]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  const changeStatus = async (lead, s) => {
    try {
      await api.patch(`/admin/crm/leads/${lead.id}`, { status: s });
      toast.success(`${lead.name} → ${STATUSES.find((x) => x.value === s)?.label}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeAppStatus = async (app, s) => {
    try {
      await api.patch(`/admin/crm/applications/${app.id}`, { status: s });
      toast.success("Candidatura aggiornata");
      loadApps();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/crm/leads", addForm);
      toast.success("Contatto creato");
      setAdding(false);
      setAddForm({ ...EMPTY });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await api.post(`/admin/crm/leads/${noteFor.id}/notes`, { text: noteText });
      setNoteText("");
      toast.success("Nota aggiunta");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminare il contatto "${l.name}"?`)) return;
    try {
      await api.delete(`/admin/crm/leads/${l.id}`);
      toast.success("Contatto eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openInCrm = (app) => {
    setAppDetail(null);
    setView("leads");
    setQ(app.email);
    setStatus("");
  };

  return (
    <div data-testid="admin-crm">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl">CRM Clienti</h1>
          <p className="text-cream2 text-sm mt-1">Contatti, candidature e note operative del team.</p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-copper !px-5 !py-2.5" data-testid="crm-add-client-btn"><Plus className="h-4 w-4" /> Nuovo contatto</button>
      </div>

      <div className="flex gap-2 mt-8" data-testid="crm-views">
        <button onClick={() => setView("leads")} className={"font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (view === "leads" ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid="crm-view-leads">
          Contatti
        </button>
        <button onClick={() => setView("apps")} className={"font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (view === "apps" ? "border-copper text-copperlight" : "border-white/10 text-cream2")} data-testid="crm-view-apps">
          Candidature
        </button>
      </div>

      {view === "leads" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-6">
            {STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => setStatus(status === s.value ? "" : s.value)}
                data-testid={`crm-status-filter-${s.value}`}
                className={"card-dark p-4 text-left transition-colors " + (status === s.value ? "border-copper/60" : "hover:border-copper/30")}
              >
                <p className="font-serif text-3xl text-cream">{stats ? stats.by_status[s.value] : "—"}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-sand mt-1">{s.label}</p>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <input className="field !w-auto sm:!w-72" placeholder="Cerca nome o email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search" />
            {status && <span className="chip">Filtro: {STATUSES.find((s) => s.value === status)?.label}</span>}
          </div>

          <div className="card-dark mt-5 overflow-x-auto" data-testid="crm-table">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-white/5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
                  <th className="px-5 py-4">Contatto</th>
                  <th className="px-5 py-4">Destinazione</th>
                  <th className="px-5 py-4">Budget</th>
                  <th className="px-5 py-4">Origine</th>
                  <th className="px-5 py-4">Stato</th>
                  <th className="px-5 py-4">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`crm-lead-card-${l.id}`}>
                    <td className="px-5 py-4">
                      <p className="text-cream">{l.name}</p>
                      <p className="text-sand text-xs mt-0.5">{l.email} {l.phone ? `· ${l.phone}` : ""}</p>
                    </td>
                    <td className="px-5 py-4 text-cream2 capitalize">{(l.destination_interest || "—").replace(/-/g, " ")}</td>
                    <td className="px-5 py-4 text-cream2">{l.budget || "—"}</td>
                    <td className="px-5 py-4"><span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/10 text-sand">{l.source === "candidatura" ? "Candidatura" : l.source === "form" ? "Form sito" : "Manuale"}</span></td>
                    <td className="px-5 py-4">
                      <select className="field !py-1.5 !px-2 !text-xs" value={l.status} onChange={(e) => changeStatus(l, e.target.value)} data-testid={`crm-status-select-${l.id}`}>
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setNoteFor(l); setNoteText(""); }} className="p-2 text-cream2 hover:text-copper" title="Note" data-testid={`crm-notes-btn-${l.id}`}><StickyNote className="h-4 w-4" /></button>
                        {isAdmin && (
                          <button onClick={() => remove(l)} className="p-2 text-cream2 hover:text-red-400" title="Elimina" data-testid={`crm-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan="6" className="px-5 py-10 text-center text-sand">Nessun contatto con questi filtri.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {stats && (
            <p className="font-mono text-[11px] text-sand mt-4" data-testid="crm-conversion">
              Conversione complessiva: <span className="text-copper">{stats.conversion}%</span> · Contatti totali: {stats.leads_total}
            </p>
          )}
        </>
      )}

      {view === "apps" && (
        <div className="card-dark mt-6 overflow-x-auto" data-testid="crm-apps-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-white/5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
                <th className="px-5 py-4">Aspirante</th>
                <th className="px-5 py-4">Spedizione</th>
                <th className="px-5 py-4">Ricevuta il</th>
                <th className="px-5 py-4">Stato</th>
                <th className="px-5 py-4">Risposte</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`crm-app-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <p className="text-cream">{a.name}</p>
                    <p className="text-sand text-xs mt-0.5">{a.email} {a.phone ? `· ${a.phone}` : ""}</p>
                  </td>
                  <td className="px-5 py-4 text-cream2">{a.tour_title}</td>
                  <td className="px-5 py-4 text-cream2">{new Date(a.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-5 py-4">
                    <select className="field !py-1.5 !px-2 !text-xs" value={a.status} onChange={(e) => changeAppStatus(a, e.target.value)} data-testid={`crm-app-status-${a.id}`}>
                      {APP_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setAppDetail(a)} className="p-2 text-cream2 hover:text-copper" title="Leggi risposte" data-testid={`crm-app-detail-${a.id}`}><FileText className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {apps.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-sand">Nessuna candidatura ricevuta per ora.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="crm-add-modal">
          <div className="max-w-lg mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Nuovo contatto</h2>
              <button onClick={() => setAdding(false)} className="text-sand hover:text-cream" data-testid="crm-add-close"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={addLead} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="label">Nome *</label><input required className="field" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} data-testid="crm-add-name" /></div>
              <div><label className="label">Email</label><input type="email" className="field" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} data-testid="crm-add-email" /></div>
              <div><label className="label">Telefono</label><input className="field" value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} data-testid="crm-add-phone" /></div>
              <div><label className="label">Destinazione</label><input className="field" value={addForm.destination_interest} onChange={(e) => setAddForm((f) => ({ ...f, destination_interest: e.target.value }))} data-testid="crm-add-destination" /></div>
              <div><label className="label">Budget</label><input className="field" value={addForm.budget} onChange={(e) => setAddForm((f) => ({ ...f, budget: e.target.value }))} data-testid="crm-add-budget" /></div>
              <div>
                <label className="label">Stato</label>
                <select className="field" value={addForm.status} onChange={(e) => setAddForm((f) => ({ ...f, status: e.target.value }))} data-testid="crm-add-status">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className="label">Messaggio</label><textarea className="field resize-none" rows="3" value={addForm.message} onChange={(e) => setAddForm((f) => ({ ...f, message: e.target.value }))} data-testid="crm-add-message" /></div>
              <div className="sm:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={() => setAdding(false)} className="btn-ghost !px-5 !py-2.5" data-testid="crm-add-cancel">Annulla</button>
                <button type="submit" disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="crm-add-submit">{busy ? "Salvo…" : "Crea contatto"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {noteFor && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="crm-notes-modal">
          <div className="max-w-lg mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Note · {noteFor.name}</h2>
              <button onClick={() => setNoteFor(null)} className="text-sand hover:text-cream" data-testid="crm-notes-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="crm-notes-list">
              {(noteFor.notes || []).map((n, i) => (
                <div key={i} className="bg-obsidian2 border border-white/5 p-3">
                  <p className="text-sm text-cream">{n.text}</p>
                  <p className="font-mono text-[10px] text-sand mt-1">{n.author} · {new Date(n.created_at).toLocaleDateString("it-IT")}</p>
                </div>
              ))}
              {(!noteFor.notes || noteFor.notes.length === 0) && <p className="text-sand text-sm">Nessuna nota.</p>}
            </div>
            <form onSubmit={addNote} className="mt-5">
              <textarea className="field resize-none" rows="3" placeholder="Scrivi una nota operativa…" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="crm-note-input" />
              <button type="submit" className="btn-copper mt-3 !px-5 !py-2.5" data-testid="crm-note-submit">Aggiungi nota</button>
            </form>
          </div>
        </div>
      )}

      {appDetail && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="crm-app-modal">
          <div className="max-w-lg mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Candidatura · {appDetail.name}</h2>
              <button onClick={() => setAppDetail(null)} className="text-sand hover:text-cream" data-testid="crm-app-modal-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-copperlight">{appDetail.tour_title}</p>
            <div className="mt-5 space-y-3" data-testid="crm-app-answers">
              <div className="bg-obsidian2 border border-white/5 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sand">Telefono</p>
                <p className="text-sm text-cream mt-1">{appDetail.phone || "—"}</p>
              </div>
              {(appDetail.answers || []).map((a, i) => (
                <div key={i} className="bg-obsidian2 border border-white/5 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sand">{a.label}</p>
                  <p className="text-sm text-cream mt-1 whitespace-pre-line">{a.value}</p>
                </div>
              ))}
            </div>
            {(appDetail.replied_at || appDetail.status === "accettata" || appDetail.status === "rifiutata") && (
              <div className="mt-6 border border-copper/30 bg-obsidian2 p-4" data-testid="crm-app-replied">
                <p className="font-mono text-[10px] uppercase tracking-widest text-copper">
                  {appDetail.replied_template === "accettata" ? "Email di accettazione inviata" : appDetail.replied_template === "rifiutata" ? "Email di rifiuto inviata" : "Nessuna email di risposta ancora"}{appDetail.replied_at ? ` · ${new Date(appDetail.replied_at).toLocaleDateString("it-IT")}` : ""}
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              {["accettata", "rifiutata"].map((tpl) => (
                <button
                  key={tpl}
                  onClick={async () => {
                    if (!window.confirm(tpl === "accettata" ? `Inviare l'email di accettazione a ${appDetail.email}?` : `Inviare l'email di rifiuto a ${appDetail.email}?`)) return;
                    try {
                      const res = await api.post(`/admin/crm/applications/${appDetail.id}/reply`, { template: tpl });
                      setAppDetail(res.data);
                      toast.success("Email inviata e stato aggiornato");
                      loadApps();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className={(tpl === "accettata" ? "btn-copper" : "btn-ghost !text-red-300 hover:!border-red-400") + " !px-4 !py-2.5 !text-xs"}
                  data-testid={`crm-app-reply-${tpl}`}
                >
                  {tpl === "accettata" ? "Invia accettazione" : "Invia rifiuto"}
                </button>
              ))}
            </div>
            <div className="flex justify-between items-center mt-6">
              <button onClick={() => openInCrm(appDetail)} className="btn-ghost !px-5 !py-2.5" data-testid="crm-app-open-lead">Apri nel CRM</button>
              <button onClick={() => setAppDetail(null)} className="font-mono text-[10px] uppercase tracking-widest text-sand hover:text-cream" data-testid="crm-app-modal-ok">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
